import { ArrowLeft, CalendarDays } from "lucide-react";
import { Link, useParams } from "react-router";
import { Avatar, Faq, PhotoTile, ServiceMenu, StylistCard } from "@/components/blocks";
import { Alert, Card, Container, Eyebrow, LinkButton, SectionHeading, Spinner } from "@/components/ui";
import { site } from "@/content";
import { useSalon } from "@/hooks/useSalon";
import { hoursLabel, WEEK } from "@/lib/format";

function PageHero({ eyebrow, title, intro }: { eyebrow: string; title: string; intro?: string }) {
  return (
    <section className="bg-[radial-gradient(60%_80%_at_80%_0%,#f6e9e6,transparent)]">
      <Container className="py-16 md:py-20">
        <div className="animate-fade-up">
          <SectionHeading eyebrow={eyebrow} title={title} intro={intro} />
        </div>
      </Container>
    </section>
  );
}

function LoadError() {
  return (
    <Alert tone="error">
      We couldn't load this right now. Please refresh, or call us at <a href={`tel:${site.phone}`} className="font-semibold underline">{site.phoneDisplay}</a>.
    </Alert>
  );
}

export function ServicesPage() {
  const { data, isLoading, isError } = useSalon();
  return (
    <>
      <PageHero
        eyebrow="Services & prices"
        title="Cuts, color, curls and classic sets."
        intro="Everything we offer, with honest prices. Pick a service to book it with your favorite stylist."
      />
      <Container>
        {isLoading && <Spinner label="Loading services" />}
        {isError && <LoadError />}
        {data && <ServiceMenu services={data.services} />}
        <p className="mt-6 text-sm text-muted">Prices marked “+” start at that amount and depend on hair length and products. Sample prices for preview.</p>
      </Container>
    </>
  );
}

export function TeamPage() {
  const { data, isLoading, isError } = useSalon();
  return (
    <>
      <PageHero eyebrow="The team" title="The people behind the chair." intro="Every stylist brings their own specialty, and the same small-town care." />
      <Container>
        {isLoading && <Spinner label="Loading team" />}
        {isError && <LoadError />}
        <div className="grid gap-6 md:grid-cols-3">{data?.stylists.map((s) => <StylistCard key={s.id} stylist={s} />)}</div>
      </Container>
    </>
  );
}

export function StylistPage() {
  const { id } = useParams();
  const { data, isLoading } = useSalon();
  const stylist = data?.stylists.find((s) => s.id === id);
  const services = data?.services.filter((s) => stylist && s.stylistIds.includes(stylist.id)) ?? [];

  if (isLoading) return <Spinner />;
  if (!stylist) return <NotFound />;

  return (
    <Container className="py-12">
      <Link to="/team" className="inline-flex items-center gap-1 text-sm font-semibold text-muted hover:text-rust">
        <ArrowLeft className="size-4" aria-hidden /> All stylists
      </Link>
      <div className="mt-8 grid gap-12 md:grid-cols-[1fr_1.4fr]">
        <div>
          {stylist.photoUrl ? (
            <img src={stylist.photoUrl} alt={stylist.name} className="aspect-[4/5] w-full rounded-[var(--radius-card)] object-cover" />
          ) : (
            <Card className="grid aspect-[4/5] place-items-center">
              <Avatar name={stylist.name} color={stylist.color} size="xl" />
            </Card>
          )}
        </div>
        <div className="animate-fade-up">
          <Eyebrow>{stylist.title}</Eyebrow>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">{stylist.name}</h1>
          <p className="mt-6 text-lg text-muted">{stylist.bio}</p>
          {stylist.bookable && (
            <LinkButton to={`/book?stylist=${stylist.id}`} size="lg" className="mt-8">
              <CalendarDays className="size-5" aria-hidden /> Book with {stylist.name.split(" ")[0]}
            </LinkButton>
          )}
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            <Card className="p-6">
              <h2 className="eyebrow text-steel">Specialties</h2>
              <ul className="mt-4 space-y-2">
                {services.map((s) => (
                  <li key={s.id}>
                    <Link to={`/book?service=${s.id}&stylist=${stylist.id}`} className="font-semibold hover:text-rust">
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-6">
              <h2 className="eyebrow text-steel">In the salon</h2>
              <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {WEEK.map((d) => (
                  <div key={d.key} className="contents">
                    <dt className="font-semibold">{d.short}</dt>
                    <dd className="text-muted">{hoursLabel(stylist.hours, d.key)}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          </div>
        </div>
      </div>
    </Container>
  );
}

export function ReviewsPage() {
  return (
    <>
      <PageHero eyebrow="Reviews" title="What our neighbors say." intro="Fifty years of happy regulars. Here's what they tell their friends." />
      <Container>
        <div className="grid gap-6 md:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <Card key={n} className="flex min-h-56 flex-col justify-between p-6">
              <p className="text-muted italic">Your Google and Facebook reviews will appear here once the salon connects them.</p>
              <p className="eyebrow mt-6 text-[0.7rem] text-steel">Review slot {n}</p>
            </Card>
          ))}
        </div>
        <Card className="mt-10 flex flex-col items-start gap-4 p-8 sm:flex-row sm:items-center">
          <p className="flex-1 font-display text-2xl">Had a great visit? We'd love to hear about it.</p>
          <a href={site.facebookUrl} target="_blank" rel="noreferrer" className="inline-flex h-12 items-center rounded-full bg-rust px-6 font-semibold text-white">
            Review us on Facebook
          </a>
        </Card>
      </Container>
    </>
  );
}

export function VisitPage() {
  const { data } = useSalon();
  return (
    <>
      <PageHero eyebrow="Hours & visit" title="Come see us on 4th Street." intro="Book ahead to skip the wait, or call and we'll fit you in." />
      <Container className="grid gap-8 md:grid-cols-2">
        <Card className="p-8">
          <h2 className="font-display text-3xl">Hours</h2>
          <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
            {WEEK.map((d) => (
              <div key={d.key} className="contents">
                <dt className="font-semibold">{d.label}</dt>
                <dd className="text-muted">{data ? hoursLabel(data.salon.hours, d.key) : "…"}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <LinkButton to="/book" size="lg">
              Book online
            </LinkButton>
            <a href={`tel:${site.phone}`} className="inline-flex h-14 items-center justify-center rounded-full font-semibold ring-1 ring-line hover:text-rust">
              Call {site.phoneDisplay}
            </a>
          </div>
        </Card>
        <Card className="overflow-hidden">
          <iframe title="Map to Style & Curl" src={site.mapsEmbed} className="h-full min-h-96 w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </Card>
      </Container>
      <Container className="mt-20">
        <SectionHeading eyebrow="Questions" title="Good to know." />
        <div className="mt-10">
          <Faq />
        </div>
      </Container>
      <Container className="mt-16 grid gap-4 sm:grid-cols-3">
        <PhotoTile label="Storefront" className="min-h-48" />
        <PhotoTile label="Inside the salon" tone="sand" className="min-h-48" />
        <PhotoTile label="The chair" tone="ink" className="min-h-48" />
      </Container>
    </>
  );
}

export function NotFound() {
  return (
    <Container className="py-24 text-center">
      <p className="font-script text-6xl text-rust">Oops!</p>
      <h1 className="mt-4 font-display text-4xl">We couldn't find that page.</h1>
      <LinkButton to="/" className="mt-8">
        Back home
      </LinkButton>
    </Container>
  );
}
